import { useState } from "react";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { LINKS, SOCIALS } from "@/data/content";
import { SocialIcon } from "@/components/Icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contatti() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [privacy, setPrivacy] = useState(false);
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!privacy) {
      toast.error("Per favore accetta la Privacy Policy per continuare.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato. Ti risponderò il prima possibile, grazie!");
      setForm({ name: "", email: "", message: "" });
      setPrivacy(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Qualcosa non ha funzionato. Riprova tra poco o scrivimi su WhatsApp."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contatti-page">
      <Seo
        title="Parliamo — Contatti | Laure Hila"
        description="Vuoi conoscermi meglio o saperne di più sul mondo dei viaggi? Scrivimi: ti rispondo personalmente, senza impegno."
      />

      <section className="pt-32 md:pt-44 pb-14 md:pb-20 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="eyebrow mb-5">Contatti</p>
          </FadeIn>
          <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
            <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
              Parliamo<span className="italic text-gold">.</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.4}>
            <p className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-stone">
              Che sia una curiosità sui viaggi, una domanda sul mio percorso o
              semplicemente voglia di fare due chiacchiere: scrivimi. Rispondo
              personalmente, con calma e senza impegno.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <a
                href={LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contatti-whatsapp-cta"
                className="flex items-center gap-4 p-6 rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 hover:border-[#25D366] transition-colors duration-300 group"
              >
                <span className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <MessageCircle size={22} />
                </span>
                <span>
                  <span className="block font-semibold text-ink">WhatsApp</span>
                  <span className="block text-sm text-stone mt-0.5">
                    Il modo più veloce per sentirci
                  </span>
                </span>
              </a>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="mt-10 mb-5 text-xs uppercase tracking-[0.22em] text-stone font-semibold">
                Seguimi sui social
              </p>
              <div className="grid grid-cols-1 gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contatti-social-${s.icon}`}
                    className="flex items-center gap-4 p-4 rounded-xl border border-line hover:border-gold transition-colors duration-300 group"
                  >
                    <span className="w-10 h-10 rounded-full bg-sand flex items-center justify-center text-ink group-hover:text-gold transition-colors duration-300">
                      <SocialIcon icon={s.icon} size={18} />
                    </span>
                    <span>
                      <span className="block font-semibold text-ink text-sm">{s.name}</span>
                      <span className="block text-xs text-stone mt-0.5">{s.handle}</span>
                    </span>
                  </a>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="p-7 md:p-10 rounded-2xl bg-sand/70 border border-line"
            >
              <h2 className="font-serif text-3xl text-ink">Scrivimi un messaggio</h2>
              <p className="mt-2 text-sm text-stone">
                Compila il modulo: il messaggio arriva direttamente a me.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">
                    Nome
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Il tuo nome"
                    className="w-full px-4 py-3.5 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 text-ink placeholder:text-stone/50 transition-[border-color,box-shadow] duration-300"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="la.tua@email.it"
                    className="w-full px-4 py-3.5 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 text-ink placeholder:text-stone/50 transition-[border-color,box-shadow] duration-300"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2">
                  Messaggio
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  minLength={10}
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Ciao Laure, vorrei saperne di più su…"
                  className="w-full px-4 py-3.5 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 text-ink placeholder:text-stone/50 transition-[border-color,box-shadow] duration-300 resize-y"
                />
              </div>

              <label className="mt-5 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="contact-privacy-checkbox"
                  checked={privacy}
                  onChange={(e) => setPrivacy(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#C5A059]"
                />
                <span className="text-xs leading-relaxed text-stone">
                  Ho letto la{" "}
                  <a href="/privacy-policy" className="underline underline-offset-2 hover:text-gold">
                    Privacy Policy
                  </a>{" "}
                  e acconsento al trattamento dei miei dati per essere ricontattata/o.
                </span>
              </label>

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full bg-ink text-cream font-semibold text-sm tracking-wide hover:bg-gold disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {sending ? (
                  <>
                    <Loader2 size={17} className="animate-spin" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Invia il messaggio
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
